import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';

import { Button, LoadingCommon, Text, View, Checkbox } from 'src/components/common';
import { IRootState } from 'src/redux/rootReducer';
import Sidebar from './Sidebar';
import './styles.scss';
import { getDailyTableDate, getDailyTimesheetPayload, INITIAL_DAILY_TIMESHEET, weeklyTableHeader } from './helpers';
import {
  TimesheetModalType,
  TimesheetModalTypes,
  WeeklyTimesheetPayload,
  DailyTimesheet,
  DeleteDailyTimesheetByIdPayload,
} from 'src/redux/timesheet/types';
import DailyTimesheetModal from './DailyTimesheetModal';
import LayoutWithSidebar from 'src/layout/LayoutWithSidebar';
import { Navigator } from 'src/services';
import { PATHS } from 'src/appConfig/paths';
import {
  addWeeklyTimesheetAsync,
  deleteDailyTimesheetsInWeekByIdAsync,
  getDailyTimesheetsByWeeklyAsync,
  getWeeklyTimesheetsByIdAsync,
  setSelectedDailyTimesheet,
  setSelectedTimesheet,
  setTimesheetModalType,
} from 'src/redux/timesheet/actions';
import { useComponentWillUnmount } from 'src/hooks';
import { getRowJob } from './RowJob';
import { getDatePayload } from 'src/utils/momentUtils';
import { getTasksAsync } from 'src/redux/task/actions';
import { getJobsAsync } from 'src/redux/jobcodes/actions';
import { getPhasesAsync } from 'src/redux/phasecodes/actions';
import { GetTasksParams } from 'src/redux/task/types';
import { GetJobsParams } from 'src/redux/jobcodes/types';
import { GetPhasesParams } from 'src/redux/phasecodes/types';
import { InitialWeeklyTableData } from 'src/redux/timesheet/reducer';
import modalActions from 'src/redux/modal/actions';
import { MODAL_TYPES } from 'src/appConfig/modal';
interface ParamTypes {
  id: string;
}
const TimesheetContainers: React.FC<Props> = ({
  user,
  data,
  loading,
  jobs,
  phases,
  tasks,
  onSetSelectedDailyData,
  onSetOpenModal,
  onGetDailyTimesheet,
  onGetWeeklyTimesheetDetail,
  onAddWeeklyTimesheet,
  onSetWeeklyData,
  onGetAllTasks,
  onGetAllJobs,
  onGetAllPharseCodes,
  onShowConfirmModal,
  onCloseConfirmModal,
  onDeleteDailyTimeSheetInWeekById,
}) => {
  const { id } = useParams<ParamTypes>();
  const isAmin = user?.role === 'ADMIN';

  useComponentWillUnmount(() => onSetOpenModal(null as TimesheetModalType));

  useEffect(() => {
    onGetAllTasks({} as GetTasksParams);
    onGetAllJobs({} as GetJobsParams);
    onGetAllPharseCodes({} as GetPhasesParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      onGetWeeklyTimesheetDetail({ id: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!!data.id) onGetDailyTimesheet({ id: data.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.id]);

  useEffect(() => {
    onGetAllTasks({} as GetTasksParams);
    onGetAllJobs({} as GetJobsParams);
    onGetAllPharseCodes({} as GetPhasesParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showRowIndex, setShowRowIndex] = useState<number[]>([]);
  const records = data.dailyTimes;

  const convertedData = getDailyTableDate(records, showRowIndex);

  const header = useMemo(
    () =>
      weeklyTableHeader.map(list => (
        <TableRow>
          <TableCell />
          {list.map(item => (
            <TableCell colSpan={item.colSpan} rowSpan={item.rowSpan}>
              {item.title}
              {item.subTitle && (
                <span className="cmp-count-table__header--light">
                  <br />
                  {item.subTitle}
                </span>
              )}
            </TableCell>
          ))}
        </TableRow>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weeklyTableHeader],
  );

  const handleEditRow = (data: any) => {
    onSetSelectedDailyData(data);
    onSetOpenModal(TimesheetModalTypes.EDIT as TimesheetModalType);
  };

  const handleShowDetail = (index: number) => {
    let foundRowIndex = showRowIndex.findIndex(i => i === index);
    let newShowRowIndex = foundRowIndex >= 0 ? showRowIndex.filter(i => !(i === index)) : [...showRowIndex, index];

    setShowRowIndex(newShowRowIndex);
  };

  const handleShowAllDetails = () => {
    setShowRowIndex(showRowIndex.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6]);
  };

  const onDeleteRow = ({
    dailyTimesheetId,
    weeklyTimesheetId,
    date,
  }: DeleteDailyTimesheetByIdPayload & { date: string }) => {
    dailyTimesheetId
      ? onShowConfirmModal({
          type: MODAL_TYPES.yesNoModal,
          data: {
            title: 'Delete Daily Timesheet',
            message: `Are you sure that you want to delete all record on \n ${date}?`,
            onOk: () => handleDeleteRow({ dailyTimesheetId, weeklyTimesheetId }),
            onCancel: onCloseConfirmModal,
          },
        })
      : onShowConfirmModal({
          type: MODAL_TYPES.okModal,
          data: {
            title: 'Notify',
            message: 'There are no records to delete',
            onOk: onCloseConfirmModal,
          },
        });
  };

  const handleDeleteRow = (deleteDailyTimesheetByIdPayload: DeleteDailyTimesheetByIdPayload) => {
    onCloseConfirmModal();
    onDeleteDailyTimeSheetInWeekById(deleteDailyTimesheetByIdPayload);
  };

  const body = convertedData.map(record =>
    getRowJob({ data: record, handleEditRow, handleShowDetail, onDeleteRow, jobs, tasks, phases }),
  );

  const handleCloseDailyModal = () => {
    onSetSelectedDailyData(INITIAL_DAILY_TIMESHEET);
    onSetOpenModal(false as TimesheetModalType);
  };

  const handleCreateWeeklyTimesheet = () => {
    const payload: WeeklyTimesheetPayload = {
      employeeId: data.employeeInfo.id,
      dailyTimesheets: getDailyTimesheetPayload(data.dailyTimes),
      startDate: getDatePayload(data.startDate),
      endDate: getDatePayload(data.endDate),
    };

    onAddWeeklyTimesheet(payload);
  };

  const handleSaveDailyTimeSheet = (value: DailyTimesheet) => {
    const updatedDailyTime = records.map(record => {
      if (record.date === value.date) return { ...value };
      return record;
    });

    onSetWeeklyData({ ...data, dailyTimes: updatedDailyTime });
    handleCloseDailyModal();
  };

  const renderTotal = useMemo(() => {
    let totalRegularHours = 0;
    let totalOTHours = 0;
    convertedData.forEach(element => {
      totalRegularHours += element.regularHours;
      totalOTHours += element.otHours;
    });

    return (
      <TableRow>
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell>TOTAL</TableCell>
        <TableCell>
          <b>{totalRegularHours}</b>
        </TableCell>
        <TableCell>
          <b>{totalOTHours}</b>
        </TableCell>
        <TableCell />
      </TableRow>
    );
    // eslint-disable-next-line
  }, [records]);

  const renderBody = () => {
    return loading ? (
      <LoadingCommon />
    ) : (
      <View className="ctn-timesheet-weekly__table ">
        <View className="table-header">
          <Text size={20}>Daily Summary</Text>
          <Checkbox.Item checked={showRowIndex.length === 7} onChange={handleShowAllDetails} label="Expand All" />
        </View>
        <Table>
          <TableHead>{header}</TableHead>
          <TableBody>
            {body}
            {renderTotal}
          </TableBody>
        </Table>
      </View>
    );
    // eslint-disable-next-line
  };

  const renderFooter = () => {
    return (
      <View className="cmp-form-footer container">
        {id && <View />}
        <Button
          variant="outline"
          label={id ? `BACK TO ${isAmin ? 'MANAGE' : 'OPEN'} TIMESHEETS` : 'CANCEL'}
          onClick={() => {
            onSetWeeklyData(InitialWeeklyTableData);
            Navigator.navigate(PATHS.homeTimesheet);
          }}
        />
        {!id && <Button label={'CREATE'} onClick={handleCreateWeeklyTimesheet} disabled={!!!data.employeeInfo.id} />}
      </View>
    );
  };

  return (
    <View className="container ctn-home is-wider-screen">
      <View className="ctn-timesheet-weekly mt-32 mb-40">
        <h3 className="ctn-timesheet-weekly__title">Weekly Timesheet</h3>
        <LayoutWithSidebar sidebar={loading ? <View /> : <Sidebar />} body={renderBody()} footer={renderFooter()} />
      </View>
      <DailyTimesheetModal onClose={handleCloseDailyModal} onSave={handleSaveDailyTimeSheet} />
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const mapStateToProps = (state: IRootState) => ({
  user: state.auth.authUser,
  data: state.timesheet.weeklyTableData,
  loading: state.timesheet.isLoading,
  jobs: state.jobCodes.jobs,
  phases: state.phaseCodes.phases,
  tasks: state.task.tasks,
});

const mapDispatchToProps = {
  onGetDailyTimesheet: getDailyTimesheetsByWeeklyAsync.request,
  onAddWeeklyTimesheet: addWeeklyTimesheetAsync.request,
  onGetWeeklyTimesheetDetail: getWeeklyTimesheetsByIdAsync.request,
  onGetAllTasks: getTasksAsync.request,
  onGetAllJobs: getJobsAsync.request,
  onGetAllPharseCodes: getPhasesAsync.request,
  onSetOpenModal: setTimesheetModalType,
  onSetSelectedDailyData: setSelectedDailyTimesheet,
  onSetWeeklyData: setSelectedTimesheet,
  // onSetDailyTimeInWeekData: setDailyTimeInWeekData,
  onShowConfirmModal: modalActions.showModal,
  onCloseConfirmModal: modalActions.closeModal,
  onDeleteDailyTimeSheetInWeekById: deleteDailyTimesheetsInWeekByIdAsync.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(TimesheetContainers);
